import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, ListChecks, PackageCheck, CalendarDays,
  IndianRupee, UserCog, BarChart3, Bell, Settings, LogOut, FileText, ScrollText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/clients", icon: Users, label: "Clients", testid: "nav-clients" },
  { to: "/projects", icon: Briefcase, label: "Projects", testid: "nav-projects" },
  { to: "/tasks", icon: ListChecks, label: "Tasks", testid: "nav-tasks" },
  { to: "/deliverables", icon: PackageCheck, label: "Deliverables", testid: "nav-deliverables" },
  { to: "/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/quotations", icon: FileText, label: "Quotations", testid: "nav-quotations", finance: true },
  { to: "/finance", icon: IndianRupee, label: "Finance", testid: "nav-finance", finance: true },
  { to: "/team", icon: UserCog, label: "Team", testid: "nav-team" },
  { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
  { to: "/notifications", icon: Bell, label: "Notifications", testid: "nav-notifications" },
  { to: "/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
  { to: "/audit-log", icon: ScrollText, label: "Audit Log", testid: "nav-audit-log", superOnly: true },
];

export default function Sidebar({ collapsed = false }) {
  const { user, logout } = useAuth();
  const financeOk = user?.finance_access || user?.role === "super_admin" || user?.role === "admin";
  const isSuper = user?.role === "super_admin";

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "shrink-0 h-screen sticky top-0 border-r border-border bg-card flex flex-col",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className={cn("h-14 flex items-center border-b border-border px-4", collapsed && "justify-center px-0")}>
        <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-display font-black">D</div>
        {!collapsed && (
          <div className="ml-3">
            <div className="font-display text-base font-extrabold leading-none tracking-tight">Digitalis CRM</div>
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mt-1">Ops Console</div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 kanban-col">
        {ITEMS.map((it) => {
          if (it.finance && !financeOk) return null;
          if (it.superOnly && !isSuper) return null;
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                  isActive
                    ? "bg-accent text-accent-foreground border-l-2 border-primary pl-[10px] font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {!collapsed && <span>{it.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border p-3">
        {!collapsed && user && (
          <div className="mb-3">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              {user.role?.replace("_", " ")}
            </div>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
        >
          <LogOut size={16} strokeWidth={1.75} />
          {!collapsed && "Sign out"}
        </button>
      </div>
    </aside>
  );
}
